import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Logo } from "../components/Logo";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { ShieldCheck, KeyRound, Mail, Check } from "lucide-react";

const MFAVerify = () => {
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const challenge = location.state?.challenge_token;
  const [method, setMethod] = useState("totp");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);

  useEffect(() => {
    if (!challenge) navigate("/login", { replace: true });
  }, [challenge, navigate]);

  const sendEmail = async () => {
    setError("");
    setInfo("");
    setEmailSending(true);
    try {
      await api.post("/auth/mfa/send-email-code", { challenge_token: challenge });
      setInfo(t("auth.emailSent"));
      setMethod("email");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setEmailSending(false);
    }
  };

  const onVerify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/verify", {
        challenge_token: challenge,
        code: code.trim(),
        method,
      });
      setUser(data.user);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white grid-bg flex flex-col">
      <header className="border-b border-white/[0.08]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" data-testid="mfa-verify-home-link"><Logo size={28} /></Link>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="card-sharp p-8 lg:p-10" data-testid="mfa-verify-card">
            <div className="flex items-center gap-2.5 text-[#19A7E0]">
              <ShieldCheck className="w-4 h-4" />
              <p className="tactical-label">MFA · Verify</p>
            </div>
            <h1 className="mt-4 text-2xl lg:text-3xl tracking-tight font-medium">{t("auth.mfaVerifyTitle")}</h1>
            <p className="mt-3 text-sm text-white/60 leading-relaxed">{t("auth.mfaVerifySubtitle")}</p>

            <div className="mt-6 grid grid-cols-2 gap-px bg-white/[0.08] border border-white/[0.08] rounded-sm overflow-hidden">
              <button
                type="button"
                onClick={() => setMethod("totp")}
                data-testid="mfa-method-totp"
                className={`px-3 py-2.5 text-xs font-mono uppercase tracking-[0.18em] flex items-center justify-center gap-2 ${
                  method === "totp" ? "bg-[#19A7E0]/15 text-[#19A7E0]" : "bg-[#050505] text-white/60"
                }`}
              >
                <KeyRound className="w-3.5 h-3.5" /> {t("auth.useTotp")}
              </button>
              <button
                type="button"
                onClick={() => setMethod("email")}
                data-testid="mfa-method-email"
                className={`px-3 py-2.5 text-xs font-mono uppercase tracking-[0.18em] flex items-center justify-center gap-2 ${
                  method === "email" ? "bg-[#19A7E0]/15 text-[#19A7E0]" : "bg-[#050505] text-white/60"
                }`}
              >
                <Mail className="w-3.5 h-3.5" /> {t("auth.useEmail")}
              </button>
            </div>

            {method === "email" && (
              <button
                type="button"
                onClick={sendEmail}
                disabled={emailSending}
                data-testid="mfa-send-email-btn"
                className="mt-4 w-full px-3 py-2.5 btn-ghost text-xs font-mono uppercase tracking-[0.18em] rounded-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Mail className="w-3.5 h-3.5" />
                {emailSending ? "…" : t("auth.sendEmail")}
              </button>
            )}

            <form onSubmit={onVerify} className="mt-6 space-y-5" data-testid="mfa-verify-form">
              <div>
                <label className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/50">
                  {t("auth.code")}
                </label>
                <div className="mt-2 relative">
                  <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                  <input
                    inputMode="numeric"
                    pattern="\d*"
                    maxLength={6}
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    data-testid="mfa-verify-code-input"
                    className="w-full bg-[#0A0D14] border border-white/15 focus:border-[#19A7E0] focus:ring-1 focus:ring-[#19A7E0] rounded-sm pl-10 pr-3 py-3 text-lg font-mono tracking-[0.4em] outline-none"
                    placeholder="000000"
                  />
                </div>
              </div>

              {info && (
                <p className="text-xs text-[#19A7E0] bg-[#19A7E0]/10 border border-[#19A7E0]/30 rounded-sm px-3 py-2" data-testid="mfa-verify-info">
                  {info}
                </p>
              )}
              {error && (
                <p className="text-xs text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-sm px-3 py-2" data-testid="mfa-verify-error">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading || code.length !== 6}
                data-testid="mfa-verify-btn"
                className="w-full inline-flex items-center justify-center gap-2 btn-primary py-3 text-xs font-mono uppercase tracking-[0.18em] rounded-sm disabled:opacity-50"
              >
                <Check className="w-4 h-4" /> {t("auth.verify")}
              </button>
            </form>
          </div>
          <p className="mt-6 text-center text-[10px] font-mono uppercase tracking-[0.25em] text-white/30">
            © 2026 IN|STALROS Intelligence &amp; Integration
          </p>
        </div>
      </main>
    </div>
  );
};

export default MFAVerify;
